import React, { useState } from 'react';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from 'chart.js';
import { InputSwitch } from 'primereact/inputswitch';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import {
  usePpDashboardVisitedSchoolsQuery,
  PpDashboardVisitedSchoolsParams,
  PpBarChartParams,
} from '../Pallipaarvai.service';
import { exportToExcel, ExcelColumn } from '../../../shared/export/excel';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

interface ObsSchoolListBarChartProps {
  filterParams: PpBarChartParams;
  onDrillChange?: (districtId: string | null, blockId: string | null) => void;
}

// ── Response types ───────────────────────────────────────────

interface DistrictItem {
  district_id: number;
  district_name: string;
  observed_schools: number;
  not_observed_schools: number;
  total_schools: number;
  percentage: number;
}

interface BlockItemRes {
  block_id: number;
  block_name: string;
  observed_schools: number;
  not_observed_schools: number;
  total_schools: number;
  percentage: number;
}

interface SchoolItem {
  school_id: number;
  school_name: string;
  udise_code: number;
  is_observed: number;
}

type DrillLevel = 'district' | 'block' | 'school';

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: { enabled: true },
  },
  interaction: {
    intersect: true,
    mode: 'index' as const,
  },
  scales: {
    x: { grid: { display: false } },
    y: { beginAtZero: true },
  },
  onClick: undefined as unknown,
};

// ── Bar color based on percentage ────────────────────────────

const getBarColor = (percentage: number): string => {
  if (percentage > 80) return '#16a34a'; // green
  if (percentage >= 50) return '#f97316'; // orange
  return '#dc2626'; // red
};

// ── Determine initial drill level from filter params ─────────

const getInitialLevel = (filterParams: PpBarChartParams): DrillLevel => {
  if (filterParams.block_id) return 'school';
  if (filterParams.district_id) return 'block';
  return 'district';
};

const getInitialBreadcrumb = (filterParams: PpBarChartParams): string[] => {
  if (filterParams.block_id) return ['State', 'District', 'Block'];
  if (filterParams.district_id) return ['State', 'District'];
  return ['State'];
};

const ObsSchoolListBarChart: React.FC<ObsSchoolListBarChartProps> = ({
  filterParams,
  onDrillChange,
}) => {
  const [drillLevel, setDrillLevel] = useState<DrillLevel>(
    getInitialLevel(filterParams)
  );
  const [selectedDistrictId, setSelectedDistrictId] = useState<
    string | undefined
  >(undefined);
  const [selectedBlockId, setSelectedBlockId] = useState<string | undefined>(
    undefined
  );
  const [showNotObserved, setShowNotObserved] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<string[]>(
    getInitialBreadcrumb(filterParams)
  );
  const [viewMode, setViewMode] = useState<'chart' | 'grid'>(
    getInitialLevel(filterParams) === 'school' ? 'grid' : 'chart'
  );

  // Build params based on drill level
  const params: PpDashboardVisitedSchoolsParams = {
    acad_yr: filterParams.acad_yr,
    month_id: filterParams.month_id,
    district_id:
      drillLevel === 'district'
        ? undefined
        : (selectedDistrictId ?? filterParams.district_id),
    block_id:
      drillLevel === 'school'
        ? (selectedBlockId ?? filterParams.block_id)
        : undefined,
    category_id: filterParams.category_id,
    management_id: filterParams.management_id,
    designation_id: filterParams.designation_id,
  };

  const { data: res, isLoading } = usePpDashboardVisitedSchoolsQuery(params);
  const resultData = (res?.result ?? []) as unknown[];

  // ── Current view label ─────────────────────────────────────

  const currentViewLabel =
    drillLevel === 'district'
      ? 'State View'
      : drillLevel === 'block'
        ? 'District View'
        : 'Block View';

  // ── Bar chart click handler ────────────────────────────────

  const handleBarClick = (index: number) => {
    if (drillLevel === 'district') {
      const item = resultData[index] as DistrictItem;
      if (item) {
        setSelectedDistrictId(String(item.district_id));
        setSelectedBlockId(undefined);
        setDrillLevel('block');
        setViewMode('chart');
        setBreadcrumb(['State', item.district_name]);
        onDrillChange?.(String(item.district_id), null);
      }
    } else if (drillLevel === 'block') {
      const item = resultData[index] as BlockItemRes;
      if (item) {
        setSelectedBlockId(String(item.block_id));
        setDrillLevel('school');
        setViewMode('grid');
        setBreadcrumb((prev) => [...prev, item.block_name]);
        onDrillChange?.(selectedDistrictId ?? null, String(item.block_id));
      }
    }
  };

  // ── Grid row click handler (for district/block grid view) ──

  const handleGridRowClick = (rowData: DistrictItem | BlockItemRes) => {
    if (drillLevel === 'district') {
      const item = rowData as DistrictItem;
      setSelectedDistrictId(String(item.district_id));
      setSelectedBlockId(undefined);
      setDrillLevel('block');
      setViewMode('chart');
      setBreadcrumb(['State', item.district_name]);
      onDrillChange?.(String(item.district_id), null);
    } else if (drillLevel === 'block') {
      const item = rowData as BlockItemRes;
      setSelectedBlockId(String(item.block_id));
      setDrillLevel('school');
      setViewMode('grid');
      setBreadcrumb((prev) => [...prev, item.block_name]);
      onDrillChange?.(selectedDistrictId ?? null, String(item.block_id));
    }
  };

  // ── Breadcrumb navigation ──────────────────────────────────

  const handleBreadcrumbClick = (level: number) => {
    if (level === 0) {
      setDrillLevel('district');
      setSelectedDistrictId(undefined);
      setSelectedBlockId(undefined);
      setViewMode('chart');
      setBreadcrumb(['State']);
      onDrillChange?.(null, null);
    } else if (level === 1) {
      setDrillLevel('block');
      setSelectedBlockId(undefined);
      setViewMode('chart');
      setBreadcrumb((prev) => prev.slice(0, 2));
      onDrillChange?.(selectedDistrictId ?? null, null);
    }
  };

  // ── Build chart data ───────────────────────────────────────

  const buildChartData = () => {
    if (drillLevel === 'school') return null;

    const items = resultData as (DistrictItem | BlockItemRes)[];
    const labels = items.map((d) =>
      'district_name' in d ? d.district_name : d.block_name
    );
    const data = items.map((d) =>
      showNotObserved ? d.not_observed_schools : d.observed_schools
    );
    const colors = showNotObserved
      ? '#dc2626'
      : items.map((d) => getBarColor(d.percentage));

    return {
      labels,
      datasets: [
        {
          label: showNotObserved ? 'Not Observed Schools' : 'Observed Schools',
          data,
          backgroundColor: colors,
          borderRadius: 4,
        },
      ],
    };
  };

  const chartData = buildChartData();

  // ── Excel download handler ─────────────────────────────────

  const handleExcelDownload = () => {
    if (drillLevel === 'school') {
      const schoolColumns: ExcelColumn<SchoolItem>[] = [
        { key: 'school_name', label: 'School Name' },
        { key: 'udise_code', label: 'UDISE Code' },
        {
          key: 'is_observed',
          label: 'Status',
          format: (val) => (val === 1 ? 'Observed' : 'Not Observed'),
        },
      ];
      exportToExcel({
        data: resultData as SchoolItem[],
        columns: schoolColumns,
        fileName: `pp_school_list_${breadcrumb.join('_')}`,
      });
    } else {
      const isDistrict = drillLevel === 'district';
      const columns: ExcelColumn[] = [
        {
          key: isDistrict ? 'district_name' : 'block_name',
          label: isDistrict ? 'District' : 'Block',
        },
        { key: 'observed_schools', label: 'Observed Schools' },
        { key: 'not_observed_schools', label: 'Not Observed Schools' },
        { key: 'total_schools', label: 'Total Schools' },
        {
          key: 'percentage',
          label: 'Percentage (%)',
          format: (val) => `${val}%`,
        },
      ];
      exportToExcel({
        data: resultData as Record<string, unknown>[],
        columns,
        fileName: `pp_observed_schools_${drillLevel}_${breadcrumb.join('_')}`,
      });
    }
  };

  // ── Render ─────────────────────────────────────────────────

  return (
    <div className="card p-3 mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0 d-inline">Observed Schools</h6>
          <span
            className="badge bg-primary ms-2"
            style={{ fontSize: '11px', fontWeight: 500 }}
          >
            {currentViewLabel}
          </span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {/* Excel download */}
          <Button
            icon="pi pi-file-excel"
            tooltip="Download Excel"
            className="p-button-text p-button-success p-button-sm"
            onClick={handleExcelDownload}
            disabled={isLoading || resultData.length === 0}
          />
          {/* Chart/Grid toggle — only for district and block levels */}
          {drillLevel !== 'school' && (
            <Button
              icon={viewMode === 'chart' ? 'pi pi-table' : 'pi pi-chart-bar'}
              tooltip={viewMode === 'chart' ? 'Grid View' : 'Chart View'}
              className="p-button-text p-button-sm"
              onClick={() =>
                setViewMode(viewMode === 'chart' ? 'grid' : 'chart')
              }
            />
          )}

          {!isLoading && drillLevel !== 'school' && (
            <>
              <span className="text-muted" style={{ fontSize: '13px' }}>
                {showNotObserved ? 'Not Observed' : 'Observed'}
              </span>
              <InputSwitch
                checked={showNotObserved}
                onChange={(e) => setShowNotObserved(e.value ?? false)}
              />
            </>
          )}
        </div>
      </div>

      {/* Breadcrumb + Legend */}
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          {breadcrumb.map((crumb, idx) => (
            <span key={idx}>
              {idx < breadcrumb.length - 1 ? (
                <Button
                  label={crumb}
                  className="p-button-link p-0"
                  style={{ fontSize: '13px' }}
                  onClick={() => handleBreadcrumbClick(idx)}
                />
              ) : (
                <span
                  className="fw-semibold"
                  style={{ fontSize: '13px', color: '#4338ca' }}
                >
                  {crumb}
                </span>
              )}
              {idx < breadcrumb.length - 1 && (
                <span className="mx-1 text-muted">/</span>
              )}
            </span>
          ))}
        </div>

        {drillLevel !== 'school' && (
          <div className="d-flex gap-3" style={{ fontSize: '12px' }}>
            <span className="d-flex align-items-center gap-1">
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#16a34a',
                  borderRadius: '3px',
                  display: 'inline-block',
                }}
              />
              <span>&gt;80% On Target</span>
            </span>
            <span className="d-flex align-items-center gap-1">
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#f97316',
                  borderRadius: '3px',
                  display: 'inline-block',
                }}
              />
              <span>50-80% Moderate</span>
            </span>
            <span className="d-flex align-items-center gap-1">
              <span
                style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#dc2626',
                  borderRadius: '3px',
                  display: 'inline-block',
                }}
              />
              <span>&lt;50% Low</span>
            </span>
          </div>
        )}
      </div>

      {isLoading && <p className="text-muted">Loading...</p>}

      {/* Bar Chart view for District and Block levels */}
      {!isLoading &&
        drillLevel !== 'school' &&
        viewMode === 'chart' &&
        chartData &&
        (chartData.labels?.length ?? 0) > 0 && (
          <div style={{ height: '350px' }}>
            <Bar
              data={chartData}
              options={{
                ...chartOptions,
                onClick: (_event, elements) => {
                  if (elements.length > 0) {
                    handleBarClick(elements[0].index);
                  }
                },
              }}
            />
          </div>
        )}

      {/* Grid view for District/Block levels (toggle) */}
      {!isLoading && drillLevel !== 'school' && viewMode === 'grid' && (
        <div style={{ minHeight: '350px' }}>
          <DataTable
            value={resultData as (DistrictItem | BlockItemRes)[]}
            paginator
            rows={10}
            stripedRows
            size="small"
            scrollable
            scrollHeight="320px"
            selectionMode="single"
            onRowSelect={(e) =>
              handleGridRowClick(e.data as DistrictItem | BlockItemRes)
            }
            emptyMessage="No data found."
          >
            <Column
              header="S.No"
              body={(_row, { rowIndex }) => rowIndex + 1}
              style={{ width: '60px' }}
            />
            <Column
              field={drillLevel === 'district' ? 'district_name' : 'block_name'}
              header={drillLevel === 'district' ? 'District' : 'Block'}
              sortable
            />
            <Column
              field="observed_schools"
              header="Observed"
              sortable
              body={(row) => row.observed_schools.toLocaleString()}
            />
            <Column
              field="not_observed_schools"
              header="Not Observed"
              sortable
              body={(row) => row.not_observed_schools.toLocaleString()}
            />
            <Column
              field="total_schools"
              header="Total"
              sortable
              body={(row) => row.total_schools.toLocaleString()}
            />
            <Column
              field="percentage"
              header="%"
              sortable
              body={(row) => `${row.percentage}%`}
            />
          </DataTable>
        </div>
      )}

      {/* Grid view for School level (always grid — drilldown final level) */}
      {!isLoading && drillLevel === 'school' && (
        <DataTable
          value={resultData as SchoolItem[]}
          paginator
          rows={10}
          stripedRows
          size="small"
          emptyMessage="No schools found."
        >
          <Column
            header="S.No"
            body={(_row, { rowIndex }) => rowIndex + 1}
            style={{ width: '60px' }}
          />
          <Column field="school_name" header="School Name" sortable />
          <Column field="udise_code" header="UDISE Code" sortable />
          <Column
            field="is_observed"
            header="Status"
            sortable
            body={(row: SchoolItem) => (
              <span
                className={`badge ${row.is_observed === 1 ? 'bg-success' : 'bg-danger'}`}
              >
                {row.is_observed === 1 ? 'Observed' : 'Not Observed'}
              </span>
            )}
          />
        </DataTable>
      )}
    </div>
  );
};

export default ObsSchoolListBarChart;
